import {
  ComponentType,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useState,
} from 'react';
import styled from 'styled-components';

import { LegacyButton as Button } from 'components/Button/LegacyButton';
import { colors, transition } from 'theme/theme';
import { ClickOutside } from 'utils/ClickOutside';

import {
  DesktopMobilePlacement,
  DropdownContainer,
  DropdownItemsContainer,
  DropdownMenuContainer,
  MenuList,
} from './Dropdown.styled';
import { Placement } from './DropdownTypes';
import { Trigger } from './Trigger';

type Props = {
  title: (open: boolean) => ReactNode;
  placement?: Placement;
  mobilePlacement?: Placement;
  hideCloseTrigger?: boolean;
  unstyled?: boolean;
  render: (arg0: { open: boolean; closeMenu: () => void }) => ReactNode;
  'data-qa-id': string;
  'aria-label'?: string;
  onClick?: (e: ReactMouseEvent, open: boolean) => void;
  onClose?: () => void;
  menuContainerComponent?: ComponentType;
  noTriggerPadding?: boolean;
};

export function Dropdown({
  title,
  placement,
  mobilePlacement,
  hideCloseTrigger,
  unstyled,
  render,
  'data-qa-id': qaId,
  'aria-label': ariaLabel,
  onClick,
  onClose,
  menuContainerComponent,
  noTriggerPadding,
}: Props) {
  const placements: DesktopMobilePlacement = [
    placement || 'down',
    mobilePlacement || 'down',
  ];
  const [open, setOpen] = useState(false);

  const toggleMenu = (e: ReactMouseEvent) => {
    if (open && onClose) {
      onClose();
    }

    setOpen(!open);
    e.preventDefault();
  };

  const closeMenu = (e?: MouseEvent | TouchEvent) => {
    if (open && onClose) {
      onClose();
    }

    if (open) {
      if (e) {
        e.stopPropagation();
      }
      setOpen(false);
    }
  };

  const onTriggerClick = (e: ReactMouseEvent) => {
    toggleMenu(e);

    if (onClick) {
      onClick(e, !open);
    }
  };

  const MenuContainer = menuContainerComponent || DropdownMenuContainer;

  return (
    <DropdownContainer>
      <Trigger
        onTriggerClick={onTriggerClick}
        qaId={qaId}
        ariaLabel={ariaLabel}
        title={title(open)}
        noPadding={noTriggerPadding}
      />
      <MenuContainer $open={open} $placements={placements} data-qa-id={qaId}>
        <ClickOutside onClickOutside={closeMenu}>
          <MenuList $placements={placements}>
            {!hideCloseTrigger && (
              <Trigger
                onTriggerClick={onTriggerClick}
                qaId={qaId}
                ariaLabel={ariaLabel}
                title={title(open)}
                noPadding={noTriggerPadding}
              />
            )}
            <DropdownItemsContainer
              $unstyled={unstyled}
              $placements={placements}
            >
              {render({
                closeMenu,
                open,
              })}
            </DropdownItemsContainer>
          </MenuList>
        </ClickOutside>
      </MenuContainer>
    </DropdownContainer>
  );
}

export const DropdownItem = styled(Button)`
  color: ${colors.lightContentGrey};
  transition: all ${transition};
  &:hover {
    color: ${colors.brandBlue};
  }
  display: flex;
  align-items: center;
  padding: 5px 10px 8px;
  font-size: 14px;
  min-width: 112px;
`;
